// Use relative URLs since frontend is served by the same backend
const API_BASE_URL = '/api';
const AUTH_BASE_URL = '';

class ApiService {
  // ==================== Internal Helpers (DRY) ====================

  /**
   * Build default fetch options with credentials and JSON content-type.
   */
  getFetchOptions(options = {}) {
    // A FormData body (CSV file upload) must NOT get a forced JSON
    // content-type — the browser sets its own multipart boundary, and
    // overriding it would break parsing on the server.
    const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
    return {
      ...options,
      credentials: 'include',
      headers: {
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        ...options.headers,
      },
    };
  }

  /**
   * Core request helper — eliminates the repetitive
   * fetch → check ok → parse error → throw / return json pattern.
   *
   * @param {string} url
   * @param {object} [options]  Extra fetch options (method, body, …)
   * @param {string} [errorMsg] Fallback error message when detail is absent
   * @returns {Promise<any>}
   */
  async _request(url, options = {}, errorMsg = 'Request failed') {
    const response = await this._fetchOk(url, options, errorMsg);
    return response.json();
  }

  /** fetch + the error handling of `_request`, returning the ok Response (for callers that need headers). */
  async _fetchOk(url, options = {}, errorMsg = 'Request failed') {
    const response = await fetch(url, this.getFetchOptions(options));
    if (!response.ok) {
      let message = errorMsg;
      let body = null;
      try {
        body = await response.json();
        if (typeof body.detail === 'string') {
          message = body.detail;
        } else if (Array.isArray(body.detail)) {
          message = body.detail.map(e => `${(e.loc || []).join('.')}: ${e.msg}`).join('; ');
        } else if (typeof body.error === 'string') {
          message = body.error;
        }
      } catch {
        // response body wasn't JSON — use fallback
      }
      const err = new Error(message);
      // Additive to the `.detail`-only convention above: callers that need
      // the full structured body (e.g. checking `err.body?.code`) can read
      // it without re-parsing the response.
      err.body = body;
      err.status = response.status;
      throw err;
    }
    return response;
  }

  /** Shorthand GET */
  _get(url, errorMsg) {
    return this._request(url, {}, errorMsg);
  }

  /** Shorthand POST with JSON body */
  _post(url, body, errorMsg) {
    return this._request(url, { method: 'POST', body: JSON.stringify(body) }, errorMsg);
  }

  /** Shorthand PATCH with JSON body */
  _patch(url, body, errorMsg) {
    return this._request(url, { method: 'PATCH', body: JSON.stringify(body) }, errorMsg);
  }

  /** Shorthand DELETE */
  _delete(url, body, errorMsg) {
    const opts = { method: 'DELETE' };
    if (body !== undefined) opts.body = JSON.stringify(body);
    return this._request(url, opts, errorMsg);
  }

  // ==================== Authentication Endpoints ====================

  getCurrentUser() {
    return this._get(`${AUTH_BASE_URL}/auth/user`, 'Failed to get user info');
  }

  logout() {
    return this._request(`${AUTH_BASE_URL}/auth/logout`, { method: 'POST' }, 'Failed to logout');
  }

  // ==================== User Directory (assignment) ====================

  getAssignableUsers() {
    return this._get(`${API_BASE_URL}/users/assignable`, 'Failed to fetch assignable users');
  }

  // ==================== Admin User Management Endpoints ====================

  getUsers(includeInactive = false) {
    return this._get(`${API_BASE_URL}/admin/users?include_inactive=${includeInactive}`, 'Failed to fetch users');
  }

  createUser(userData) {
    return this._post(`${API_BASE_URL}/admin/users`, userData, 'Failed to create user');
  }

  updateUser(userId, userData) {
    return this._patch(`${API_BASE_URL}/admin/users/${userId}`, userData, 'Failed to update user');
  }

  deleteUser(userId) {
    return this._delete(`${API_BASE_URL}/admin/users/${userId}`, undefined, 'Failed to delete user');
  }

  // ==================== Report Endpoints ====================

  assignAnomaly(anomalyId, assigneeEmail) {
    return this._patch(
      `${API_BASE_URL}/anomalies/${anomalyId}/assign`,
      { assignee_email: assigneeEmail },
      'Failed to assign anomaly',
    );
  }

  bulkAssignAnomalies(anomalyIds, assigneeEmail) {
    return this._post(
      `${API_BASE_URL}/anomalies/bulk-assign`,
      { anomaly_ids: anomalyIds, assignee_email: assigneeEmail },
      'Failed to bulk assign anomalies',
    );
  }

  uploadReport(file) {
    const formData = new FormData();
    formData.append('file', file);

    return fetch(`${API_BASE_URL}/reports/upload`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
    }).then(async (response) => {
      if (!response.ok) {
        let message = 'Failed to upload report';
        try {
          const error = await response.json();
          if (typeof error.detail === 'string') {
            message = error.detail;
          } else if (Array.isArray(error.detail)) {
            message = error.detail.map(e => `${(e.loc || []).join('.')}: ${e.msg}`).join('; ');
          }
        } catch {
          // response body wasn't JSON
        }
        throw new Error(message);
      }
      return response.json();
    });
  }

  // ==================== Anomaly Endpoints ====================

  getAnomaly(anomalyId) {
    return this._get(`${API_BASE_URL}/anomalies/${anomalyId}`, 'Failed to fetch anomaly');
  }

  // ==================== Feedback Endpoints ====================

  createFeedback(feedbackData) {
    return this._post(`${API_BASE_URL}/feedback`, feedbackData, 'Failed to create feedback');
  }

  getFeedback(feedbackId) {
    return this._get(`${API_BASE_URL}/feedback/${feedbackId}`, 'Failed to fetch feedback');
  }

  getAnomalyFeedback(anomalyId) {
    return this._get(`${API_BASE_URL}/anomalies/${anomalyId}/feedback`, 'Failed to fetch feedback');
  }

  getPendingFeedback(limit = 100) {
    return this._get(`${API_BASE_URL}/feedback/pending?limit=${limit}`, 'Failed to fetch pending feedback');
  }

  dismissFeedback(feedbackId, dismissData) {
    return this._post(`${API_BASE_URL}/feedback/${feedbackId}/dismiss`, dismissData, 'Failed to dismiss feedback');
  }

  getFeedbackSummary() {
    return this._get(`${API_BASE_URL}/feedback/summary`, 'Failed to fetch feedback summary');
  }

  // ==================== Rule Endpoints ====================

  createRule(ruleData) {
    return this._post(`${API_BASE_URL}/rules`, ruleData, 'Failed to create rule');
  }

  createRuleFromFeedback(feedbackId, ruleData) {
    return this._post(`${API_BASE_URL}/feedback/${feedbackId}/create-rule`, ruleData, 'Failed to create rule from feedback');
  }

  getGlobalRules(pageType = null, retailer = null) {
    const params = new URLSearchParams();
    if (pageType) params.set('page_type', pageType);
    if (retailer) params.set('retailer', retailer);
    const qs = params.toString() ? `?${params.toString()}` : '';
    return this._get(`${API_BASE_URL}/rules/global${qs}`, 'Failed to fetch global rules');
  }

  getAttributeRules(attributeType, pageType = null, retailer = null) {
    const params = new URLSearchParams();
    if (pageType) params.set('page_type', pageType);
    if (retailer) params.set('retailer', retailer);
    const qs = params.toString() ? `?${params.toString()}` : '';
    return this._get(`${API_BASE_URL}/rules/attribute/${attributeType}${qs}`, 'Failed to fetch attribute rules');
  }

  getAllAttributeRules(pageType = null, retailer = null) {
    const params = new URLSearchParams();
    if (pageType) params.set('page_type', pageType);
    if (retailer) params.set('retailer', retailer);
    const qs = params.toString() ? `?${params.toString()}` : '';
    return this._get(`${API_BASE_URL}/rules/all-attributes${qs}`, 'Failed to fetch attribute rules');
  }

  deactivateRule(ruleId, deactivateData) {
    return this._post(`${API_BASE_URL}/rules/${ruleId}/deactivate`, deactivateData, 'Failed to deactivate rule');
  }

  reactivateRule(ruleId, reactivateData) {
    return this._post(`${API_BASE_URL}/rules/${ruleId}/reactivate`, reactivateData, 'Failed to reactivate rule');
  }

  updateRulePriority(ruleId, priorityData) {
    return this._patch(`${API_BASE_URL}/rules/${ruleId}/priority`, priorityData, 'Failed to update rule priority');
  }

  getRuleAuditHistory(ruleId) {
    return this._get(`${API_BASE_URL}/rules/${ruleId}/audit`, 'Failed to fetch rule audit history');
  }

  getRuleContributors(ruleId) {
    return this._get(`${API_BASE_URL}/rules/${ruleId}/contributors`, 'Failed to fetch rule contributors');
  }

  getContributorFeedback(ruleId, reviewedBy) {
    return this._get(
      `${API_BASE_URL}/rules/${ruleId}/contributors/${encodeURIComponent(reviewedBy)}`,
      'Failed to fetch contributor feedback',
    );
  }

  getRulesSummary() {
    return this._get(`${API_BASE_URL}/rules/summary`, 'Failed to fetch rules summary');
  }

  // ==================== Rule Synthesis Review Endpoints ====================

  getBatchDays(startDate, endDate) {
    const params = new URLSearchParams();
    if (startDate) params.set('start_date', startDate);
    if (endDate) params.set('end_date', endDate);
    const qs = params.toString() ? `?${params.toString()}` : '';
    return this._get(`${API_BASE_URL}/rule-synthesis/days${qs}`, 'Failed to fetch batch days');
  }

  getDayDetail(date) {
    return this._get(`${API_BASE_URL}/rule-synthesis/days/${date}`, 'Failed to fetch day detail');
  }

  updateRule(ruleId, updateData) {
    return this._patch(`${API_BASE_URL}/rules/${ruleId}`, updateData, 'Failed to update rule');
  }

  deleteRule(ruleId, deleteData) {
    return this._post(`${API_BASE_URL}/rules/${ruleId}/deactivate`, deleteData, 'Failed to delete rule');
  }


  // ==================== Healing Job Endpoints ====================

  triggerHealJob({ anomalyId, retailer, attribute, pageType, issueType, records, falsePositives }) {
    const body = {
      anomaly_id: anomalyId,
      retailer,
      attribute,
      records,
      page_type: pageType || 'detail',
      issue_type: issueType || 'null-issues',
    };
    if (falsePositives && falsePositives.length > 0) {
      body.false_positives = falsePositives;
    }
    return this._post(
      `${API_BASE_URL}/heal-jobs`,
      body,
      'Failed to trigger healing job',
    );
  }

  saveSampleClassifications(anomalyId, classifications, occurrenceId = null) {
    const body = { classifications };
    if (occurrenceId) body.occurrence_id = occurrenceId;
    return this._request(
      `${API_BASE_URL}/anomalies/${anomalyId}/sample-classifications`,
      { method: 'PUT', body: JSON.stringify(body) },
      'Failed to save sample classifications',
    );
  }

  /** Submit a single review signal, scoped to one evidence sample within one
   * occurrence. Unlike saveSampleClassifications (which persists the whole
   * draft array), this writes exactly one review_signals row and never
   * touches any other sample's or occurrence's data. */
  submitReviewSignal(anomalyId, { occurrenceId, evidenceIndex, verdict, fpReason = '', scopeType = 'generic' }) {
    return this._post(
      `${API_BASE_URL}/anomalies/${anomalyId}/review-signal`,
      {
        occurrence_id: occurrenceId,
        evidence_index: evidenceIndex,
        verdict,
        fpReason,
        scope_type: scopeType,
      },
      'Failed to submit review signal',
    );
  }

  getActiveHealJob(anomalyId, { retailer, attribute } = {}) {
    const params = new URLSearchParams();
    if (retailer) params.set('retailer', retailer);
    if (attribute) params.set('attribute', attribute);
    const qs = params.toString() ? `?${params.toString()}` : '';
    return this._get(
      `${API_BASE_URL}/heal-jobs/active/${anomalyId}${qs}`,
      'Failed to check active healing job',
    );
  }

  getHealJob(jobId) {
    return this._get(`${API_BASE_URL}/heal-jobs/${jobId}`, 'Failed to fetch healing job');
  }

  /**
   * One page of healing jobs, newest first. createdFrom/createdTo are
   * 'YYYY-MM-DD' inclusive UTC days, filtered on the server.
   * Resolves to { items, total }; total is null when the hub does not know it.
   */
  async getHealJobs({ limit = 200, offset = 0, createdFrom = '', createdTo = '' } = {}) {
    const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    if (createdFrom) params.set('created_from', createdFrom);
    if (createdTo) params.set('created_to', createdTo);
    const response = await this._fetchOk(
      `${API_BASE_URL}/heal-jobs?${params.toString()}`, {}, 'Failed to fetch healing jobs',
    );
    const body = await response.json();
    const header = response.headers?.get('X-Total-Count');
    const total = header != null && /^\d+$/.test(header) ? Number(header) : null;
    return { items: Array.isArray(body) ? body : [], total };
  }

  pollHealJob(jobId) {
    return this._request(
      `${API_BASE_URL}/heal-jobs/${jobId}/poll`,
      { method: 'POST' },
      'Failed to poll job status',
    );
  }

  approveHealJob(jobId, { jiraTicket, editedConfig = null, editedParsedConfig = null }) {
    return this._post(
      `${API_BASE_URL}/heal-jobs/${jobId}/approve`,
      {
        jira_ticket: jiraTicket,
        edited_config: editedConfig,
        edited_parsed_config: editedParsedConfig,
      },
      'Failed to approve job',
    );
  }

  validateHealJobConfig(jobId, config, parsedConfig = null) {
    return this._post(
      `${API_BASE_URL}/heal-jobs/${jobId}/validate-config`,
      { config, parsed_config: parsedConfig },
      'Failed to validate config',
    );
  }

  rejectHealJob(jobId, reason, reasonCode) {
    const body = reasonCode ? { reason, reasonCode } : { reason };
    return this._post(`${API_BASE_URL}/heal-jobs/${jobId}/reject`, body, 'Failed to reject job');
  }

  /** Put a rejected job back in Awaiting Review. The hub derives the reopener from the session. */
  reopenHealJob(jobId) {
    return this._post(`${API_BASE_URL}/heal-jobs/${jobId}/reopen`, {}, 'Failed to reopen job');
  }

  reviseHealJob(jobId, feedback) {
    return this._post(`${API_BASE_URL}/heal-jobs/${jobId}/revise`, { feedback }, 'Failed to revise job');
  }

  // ==================== GitHub Credentials ====================

  getGithubCredentialStatus() {
    return this._get(`${API_BASE_URL}/users/me/github-credentials`, 'Failed to check GitHub credentials');
  }

  saveGithubCredentials(githubPat) {
    return this._request(
      `${API_BASE_URL}/users/me/github-credentials`,
      { method: 'PUT', body: JSON.stringify({ github_pat: githubPat }) },
      'Failed to save GitHub credentials',
    );
  }

  deleteGithubCredentials() {
    return this._delete(`${API_BASE_URL}/users/me/github-credentials`, undefined, 'Failed to remove GitHub credentials');
  }

  // ==================== Retailer-Attribute Skills ====================

  /**
   * Queue onboarding for one or more attributes. Returns immediately with
   * `{ accepted, queued, skipped }` — generation continues in the background.
   * @param {object} params
   * @param {string} params.retailer
   * @param {string[]} params.attributes
   * @param {string} [params.pageType]
   */
  onboardSkills({ retailer, attributes, pageType }) {
    return this._request(
      `${API_BASE_URL}/skills/onboard`,
      {
        method: 'POST',
        body: JSON.stringify({ retailer, attributes, pageType: pageType || 'detail' }),
      },
      'Failed to onboard skills',
    );
  }

  /** List all onboarded skills, optionally filtered by status. */
  getSkills(status = null) {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    const qs = params.toString() ? `?${params.toString()}` : '';
    return this._get(`${API_BASE_URL}/skills${qs}`, 'Failed to load skills');
  }

  /**
   * Fetch a single skill's row + content. On a 404 for nothing onboarded,
   * the thrown Error's `body` (attached by `_request`) carries the
   * upstream's structured payload, e.g. `{ code: 'not_onboarded', ... }` —
   * but that shape isn't guaranteed (a non-JSON 404 leaves `body` null), so
   * check `err.body?.code` defensively.
   */
  getSkill(retailer, attribute, pageType = null) {
    const params = new URLSearchParams();
    if (pageType) params.set('pageType', pageType);
    const qs = params.toString() ? `?${params.toString()}` : '';
    return this._get(
      `${API_BASE_URL}/skills/${encodeURIComponent(retailer)}/${encodeURIComponent(attribute)}${qs}`,
      'Failed to load skill',
    );
  }

  /** Update a skill's content. Returns the updated row + content. */
  updateSkill(retailer, attribute, content, pageType = null) {
    const body = { content };
    if (pageType) body.pageType = pageType;
    return this._request(
      `${API_BASE_URL}/skills/${encodeURIComponent(retailer)}/${encodeURIComponent(attribute)}`,
      { method: 'PUT', body: JSON.stringify(body) },
      'Failed to save skill',
    );
  }

  /** Delete an onboarded skill. */
  deleteSkill(retailer, attribute, pageType = null) {
    const params = new URLSearchParams();
    if (pageType) params.set('pageType', pageType);
    const qs = params.toString() ? `?${params.toString()}` : '';
    return this._delete(
      `${API_BASE_URL}/skills/${encodeURIComponent(retailer)}/${encodeURIComponent(attribute)}${qs}`,
      undefined,
      'Failed to delete skill',
    );
  }

  // ==================== CSV Bulk Skill Onboarding ====================

  /**
   * Upload a CSV (columns: retailer, page_type, attribute) for read-only
   * preview — classifies every row as already_onboarded / will_create /
   * will_inherit / error / invalid without generating anything or spending
   * any Vertex AI quota. Returns `{ rows: [...] }`.
   */
  previewSkillsCsv(file) {
    const formData = new FormData();
    formData.append('file', file);
    return this._request(
      `${API_BASE_URL}/skills/csv/preview`,
      { method: 'POST', body: formData },
      'Failed to preview CSV',
    );
  }

  /**
   * Mark the approved rows pending and enqueue one Cloud Task per row onto
   * the skill-creation queue. `triples` should be exactly the rows the user
   * approved from the preview response (each `{ retailer, pageType,
   * attribute }`), not a re-derivation from the CSV.
   */
  approveSkillsCsv(triples) {
    return this._post(`${API_BASE_URL}/skills/csv/approve`, { triples }, 'Failed to approve skill onboarding');
  }

  /** Current stored status for a batch of triples — progress polling after approve. */
  getSkillsCsvStatus(triples) {
    return this._post(`${API_BASE_URL}/skills/csv/status`, { triples }, 'Failed to load onboarding status');
  }

  // ==================== Anomaly-Level View ====================

  /** Filter params shared by the anomalies list and its CSV export (no paging). */
  _anomalyListParams({ retailer, retailer_priority, attribute, page_type, issue_type, assignee, verdict, verdict_status, review_status, sort, first_seen_from, first_seen_to, verified_from, verified_to, archived_only = false, closed_only = false, needs_revalidation = false, source, grouping_key } = {}) {
    const params = new URLSearchParams();
    // retailer / attribute / review_status may be a string or an array → repeated params (backend any-of).
    const appendAll = (key, v) => (Array.isArray(v) ? v : [v]).filter(Boolean).forEach((x) => params.append(key, x));
    appendAll('retailer', retailer);
    if (retailer_priority) params.set('retailer_priority', retailer_priority);
    appendAll('attribute', attribute);
    if (page_type) params.set('page_type', page_type);
    if (issue_type) params.set('issue_type', issue_type);
    if (assignee) params.set('assignee', assignee);
    if (verdict) params.set('verdict', verdict);
    if (verdict_status) params.set('verdict_status', verdict_status);
    appendAll('review_status', review_status);
    if (sort) params.set('sort', sort);
    if (first_seen_from) params.set('first_seen_from', first_seen_from);
    if (first_seen_to) params.set('first_seen_to', first_seen_to);
    if (verified_from) params.set('verified_from', verified_from);
    if (verified_to) params.set('verified_to', verified_to);
    if (archived_only) params.set('archived_only', 'true');
    if (closed_only) params.set('closed_only', 'true');
    if (needs_revalidation) params.set('needs_revalidation', 'true');
    if (source) params.set('source', source);
    if (grouping_key) params.set('grouping_key', grouping_key);
    return params;
  }

  getAnomalies({ limit = 50, offset = 0, ...filters } = {}) {
    const params = this._anomalyListParams(filters);
    params.set('limit', String(limit));
    params.set('offset', String(offset));
    return this._get(`${API_BASE_URL}/anomalies?${params.toString()}`, 'Failed to fetch anomalies');
  }

  /**
   * Download the anomalies matching the list filters as CSV (paging is ignored).
   * Resolves to { truncated, total, cap } from the server's export headers.
   */
  async downloadAnomaliesCsv({ limit, offset, ...filters } = {}) {
    const qs = this._anomalyListParams(filters).toString();
    const response = await this._downloadFile(
      `${API_BASE_URL}/anomalies/export.csv${qs ? `?${qs}` : ''}`,
      'anomalies.csv',
      'Failed to export anomalies',
    );
    const header = (name) => response.headers.get(name);
    return {
      truncated: header('X-Export-Truncated') === 'true',
      total: Number(header('X-Export-Total') || 0),
      cap: Number(header('X-Export-Row-Cap') || 0),
    };
  }

  getAnomalyFixDefault(anomalyId) {
    return this._get(
      `${API_BASE_URL}/anomalies/${encodeURIComponent(anomalyId)}/fix-default`,
      'Failed to fetch the suggested fixed date',
    );
  }

  /** DPS-4715: { job_id, reextraction } — the newest re-extraction plan among the anomaly's heal jobs (nulls when none). */
  getAnomalyReextraction(anomalyId) {
    return this._get(
      `${API_BASE_URL}/anomalies/${encodeURIComponent(anomalyId)}/reextraction`,
      'Failed to fetch the re-extraction status',
    );
  }

  getAnomalyOccurrences(anomalyId) {
    return this._get(`${API_BASE_URL}/anomalies/${anomalyId}/occurrences`, 'Failed to fetch occurrences');
  }

  getOccurrenceEvidence(anomalyId, occurrenceId) {
    return this._get(`${API_BASE_URL}/anomalies/${anomalyId}/occurrences/${occurrenceId}`, 'Failed to fetch occurrence evidence');
  }

  getAnomalyFilters({ source } = {}) {
    const params = new URLSearchParams();
    if (source) params.set('source', source);
    const qs = params.toString();
    return this._get(`${API_BASE_URL}/anomalies-filters${qs ? `?${qs}` : ''}`, 'Failed to fetch anomaly filters');
  }

  classifyAnomaly(anomalyId, { verdict, reason } = {}) {
    return this._post(`${API_BASE_URL}/anomalies/${anomalyId}/classify`, { verdict, reason });
  }

  resolveAnomalyConflict(anomalyId, chosenVerdict) {
    return this._post(`${API_BASE_URL}/anomalies/${anomalyId}/resolve-conflict`, { chosen_verdict: chosenVerdict });
  }

  dismissRevalidation(anomalyId) {
    return this._post(`${API_BASE_URL}/anomalies/${anomalyId}/dismiss-revalidation`, {});
  }

  markAnomalyFixed(anomalyId, data) {
    return this._post(`${API_BASE_URL}/anomalies/${anomalyId}/mark-fixed`, data);
  }

  // ==================== Metrics Dashboard ====================

  getMetrics(dateFrom, dateTo) {
    const params = new URLSearchParams();
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    const qs = params.toString();
    return this._get(
      `${API_BASE_URL}/metrics${qs ? `?${qs}` : ''}`,
      'Failed to fetch metrics',
    );
  }

  getHealingMetrics(dateFrom, dateTo) {
    const params = new URLSearchParams();
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    const qs = params.toString();
    return this._get(
      `${API_BASE_URL}/metrics/healing${qs ? `?${qs}` : ''}`,
      'Failed to fetch healing metrics',
    );
  }

  async downloadMetricsCsv(dateFrom, dateTo) {
    const params = new URLSearchParams();
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    const qs = params.toString();
    await this._downloadFile(
      `${API_BASE_URL}/metrics/csv${qs ? `?${qs}` : ''}`,
      'metrics.csv',
      'Failed to download metrics CSV',
    );
  }

  /** GET a file and save it under the server's Content-Disposition filename; returns the Response. */
  async _downloadFile(url, fallbackName, errorMsg) {
    const response = await this._fetchOk(url, {}, errorMsg);
    const blob = await response.blob();
    const disposition = response.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename="?([^"]+)"?/);
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = match?.[1] || fallbackName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objectUrl);
    return response;
  }

}

const apiService = new ApiService();
export default apiService;
